'use client';

import { TextLink, Grid } from '@petsmart-ui/sparky';

import { clsx, useCart, usePageLayout, useDeviceType, DEVICE_TYPE } from '@libs/common-next';
import type { StorageUserSession } from '@libs/common-next';
import { logoutUser, NAVIGATION_URLS } from '@libs/core';
import './LoggedIn.scss';
import { trackNavigationHeaderClick } from '@libs/analytics';
import { useEffect, useRef } from 'react';

const navigationUrls = NAVIGATION_URLS.velocity.account;

const navigationItems = [
    { label: 'About you', trackingKey: 'about you', url: navigationUrls.aboutYou },
    { label: 'Credit cards', trackingKey: 'payments', url: navigationUrls.creditCards },
    { label: 'Appointments', trackingKey: 'appointments', url: navigationUrls.servicesGrooming },
    { label: 'Autoship', trackingKey: 'autoship', url: navigationUrls.autoShip },
    { label: 'Recent orders', trackingKey: 'orders', url: navigationUrls.orderHistory },
    { label: 'Offers', trackingKey: 'offers', url: navigationUrls.offers },
];

export function LoggedIn({ firstName = '', loyaltyPoints = 0 }: Readonly<StorageUserSession>) {
    const { flags } = usePageLayout();
    const { clearCart } = useCart();
    const deviceType = useDeviceType();
    const isLoyaltyAccountMenu = flags.loyaltyAccountMenu;
    const link = useRef<string>('');

    const wrapperClasses = clsx('legacy-loggedin', isLoyaltyAccountMenu ? 'loyalty-account-menu-enabled' : '');

    useEffect(() => {
        if (typeof window !== 'undefined') {
            link.current = window.location.origin;
        }
    }, []);

    if (!firstName) {
        return null;
    }

    return (
        <div className={wrapperClasses} data-testid="logged-in">
            <div className="legacy-loggedin__button">
                {deviceType === DEVICE_TYPE.DESKTOP ? (
                    <TextLink href={navigationUrls.treatOffers}>
                        <span className="legacy-loggedin__greetings">
                            Hi,
                            <span className="legacy-loggedin__user-name" data-cs-mask>
                                {firstName}
                            </span>
                        </span>
                        <span className="legacy-account__caret" />
                    </TextLink>
                ) : (
                    <>
                        <span>
                            Hi, <span data-cs-mask>{firstName}</span>
                        </span>
                        <span className="legacy-account__caret" />
                    </>
                )}
            </div>

            <div className="legacy-loggedin__submenu">
                <a
                    className="points-link"
                    href={navigationUrls.treatOffers}
                    title="Go to: View your rewards and offers"
                >
                    <div className="points">
                        <span className="point-count">
                            {` ${new Intl.NumberFormat('en-US', {
                                maximumFractionDigits: 0,
                            }).format(loyaltyPoints || 0)} `}
                        </span>
                        pts.
                        <span className="account">{isLoyaltyAccountMenu ? 'Treats Rewards' : 'Account'}</span>
                    </div>
                </a>

                <Grid variant="1-2up" gap="none">
                    {navigationItems.map(item => (
                        <Grid.Item key={item.trackingKey}>
                            <TextLink
                                onClick={() => {
                                    trackNavigationHeaderClick(item.trackingKey, link.current + item.url);
                                }}
                                href={item.url}
                            >
                                {item.label}
                            </TextLink>
                        </Grid.Item>
                    ))}
                </Grid>
                <TextLink className="logout-link" onClick={async () => await logoutUser('/sign-in', clearCart)}>
                    Logout
                </TextLink>
            </div>

            <div className="dropdown-accent" />
        </div>
    );
}
